/**
 * Dutch copy for the fase-delete confirm dialog.
 *
 * Since Igor's 2026-09-08 amendment a fase delete cascades into its planning
 * children, and each of those is a scheduled werf-day that may already have
 * installateurs assigned to it. The dialog therefore has to say what
 * disappears, not just that something will — so this copy is load-bearing, and
 * it lives in its own module so the plural agreement at 0, 1 and many is
 * asserted on exact strings instead of being eyeballed inside a component.
 *
 * AMENDMENT 3 (Igor, 2026-09-08): the delete ARCHIVES the fase instead of
 * unlinking it. The dialog used to promise "definitief verwijderd. Dit kan niet
 * ongedaan gemaakt worden", which is now simply false, and it promised the
 * fase-bestanden would go, which they no longer do. Both are corrected here.
 * The button verb stays "Verwijderen": in this product's Dutch that already
 * means archive — the planning board's own "Verwijderde taken" drawer is
 * exactly this mechanism.
 *
 * Dates are formatted from the API's `YYYY-MM-DD` by hand rather than through
 * `new Date()` + `toLocaleDateString`: parsing a bare date string yields UTC
 * midnight, which can render as the previous day, and a destructive dialog is
 * the last place to be off by a day.
 */
import type { FasePlanningDay } from '@/api/projectFases';

/** `2026-05-04` → `04/05/2026`. Returns the input unchanged if it is not a date. */
export function formatFaseDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/**
 * How many werf-days go off the board with the fase. Agrees at 0, 1 and many.
 * "verdwijnt mee" rather than "wordt verwijderd": the day is archived with its
 * fase and, unlike an unscheduled day, it does NOT come back in the
 * "Verwijderde taken" drawer — only in Odoo, with the fase.
 */
export function faseDeletePlanningLine(days: FasePlanningDay[]): string {
  if (days.length === 0) return 'Er zijn geen geplande werfdagen bij deze fase.';
  if (days.length === 1) return '1 geplande werfdag verdwijnt mee, met de toegewezen installateurs.';
  return `${days.length} geplande werfdagen verdwijnen mee, met de toegewezen installateurs.`;
}

/**
 * The dates of the days that have one, so the user recognises the work being
 * removed. Null when no day carries a date (nothing useful to list).
 */
export function faseDeleteDatesLine(days: FasePlanningDay[]): string | null {
  const dates = days
    .map((d) => d.date)
    .filter((d): d is string => Boolean(d))
    .map(formatFaseDay);
  if (dates.length === 0) return null;
  if (dates.length === 1) return `Datum: ${dates[0]}.`;
  return `Datums: ${dates.join(', ')}.`;
}

/**
 * Days that are real work but not scheduled yet. They vanish too, and they
 * would otherwise be invisible in the dates line. Null when there are none.
 */
export function faseDeleteUndatedLine(days: FasePlanningDay[]): string | null {
  const undated = days.filter((d) => !d.date).length;
  if (undated === 0) return null;
  if (undated === 1) return 'Daarvan heeft 1 werfdag nog geen datum.';
  return `Daarvan hebben ${undated} werfdagen nog geen datum.`;
}

/**
 * What happens to the fase-bestanden: nothing. They are KEPT since amendment 3,
 * because the fase can be restored. Stated positively rather than dropped, so a
 * user who remembers the old dialog is not left wondering. -1 means storage
 * could not be listed, which is why the count is omitted in that case.
 */
export function faseDeleteFilesLine(count: number): string {
  if (count < 0) return 'Bijhorende fase-bestanden blijven bewaard.';
  if (count === 0) return 'Er zijn geen fase-bestanden bij deze fase.';
  if (count === 1) return '1 fase-bestand blijft bewaard.';
  return `${count} fase-bestanden blijven bewaard.`;
}

/**
 * The opening sentences of the confirm step: what actually happens, and that it
 * is recoverable. Two short sentences on purpose — the old single sentence
 * ("wordt definitief verwijderd. Dit kan niet ongedaan gemaakt worden.") was
 * wrong in both halves once the delete became an archive, and a destructive-
 * looking dialog that overstates its own finality trains people to distrust it.
 *
 * Recovery is in Odoo, not in the portal: a portal-side restore screen is its
 * own work item, so the copy must not imply the portal can do it.
 */
export function faseArchiveIntroLine(faseName: string): string {
  return `«${faseName}» verdwijnt van het dashboard en uit de planning. De fase blijft in Odoo bestaan en kan daar teruggezet worden.`;
}
